import React from 'react';
import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import { 
  Lock as LockIcon, 
  Settings as SettingsIcon,
  ContactSupport as ContactIcon,
  Home as HomeIcon 
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * PermissionGuard Component
 * Conditionally renders children based on access permissions
 * 
 * @param {boolean} hasAccess - Whether the user has access to the component
 * @param {React.ReactNode} children - The components to render if access is granted
 * @param {React.ReactNode} fallback - Optional fallback component to render when access is denied
 */
const PermissionGuard = ({ 
  hasAccess = false, 
  children, 
  fallback = null,
  showAccessDenied = true,
  featureName = "this feature"
}) => {
  const { t } = useTranslation();
  
  // If access is granted, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If custom fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // If showAccessDenied is false, render nothing
  if (!showAccessDenied) {
    return null;
  }

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleContactAdmin = () => {
    // You can customize this to open a contact form or redirect to support
    alert(t('contact_admin_message') || 'Please contact your system administrator to enable this feature.');
  };

  // Default access denied message
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 4,
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 6,
          textAlign: 'center',
          maxWidth: 500,
          borderRadius: 4,
          background: 'white',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          border: '1px solid #e3f2fd',
        }}
      >
                  {/* Lock Icon with Animation */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '120px',
            height: '120px',
            margin: '0 auto',
            marginBottom: 3,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              opacity: 0.1,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 0.1,
                },
                '50%': {
                  transform: 'scale(1.1)',
                  opacity: 0.2,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 0.1,
                },
              },
            }}
          />
          <LockIcon
            sx={{
              fontSize: '4rem',
              color: '#667eea',
              zIndex: 1,
              position: 'relative',
            }}
          />
        </Box>

        {/* Status Chip */}
        <Chip
          label={t('feature_disabled') || 'Feature Disabled'}
          color="warning"
          variant="outlined"
          sx={{
            marginBottom: 3,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        />

        {/* Title */}
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: '#333',
            fontWeight: 'bold',
            marginBottom: 2,
          }}
        >
          {t('access_restricted') || 'Access Restricted'}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: '#666',
            lineHeight: 1.8,
            marginBottom: 4,
            fontSize: '1.1rem',
          }}
        >
          {t('the') || 'The'} <strong>{featureName}</strong> {t('module_currently_disabled') || 'module is currently disabled. This feature needs to be enabled by your administrator before you can access it.'}
        </Typography>

        {/* Instructions */}
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            padding: 3,
            marginBottom: 4,
            border: '1px solid #e9ecef',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: '#495057',
              fontWeight: 'bold',
              marginBottom: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SettingsIcon fontSize="small" />
            {t('how_to_enable_feature') || 'How to Enable This Feature'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6c757d',
              lineHeight: 1.6,
            }}
          >
            1. {t('enable_step_1') || 'Go to Settings in the admin panel'}<br/>
            2. {t('enable_step_2') || 'Find Module Settings'}<br/>
            3. {t('enable_step_3') || 'Enable the Support Management module'}<br/>
            4. {t('enable_step_4') || 'Save changes and refresh this page'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          display="flex"
          gap={2}
          justifyContent="center"
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 'bold',
              paddingX: 3,
              paddingY: 1.5,
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('go_to_dashboard') || 'Go to Dashboard'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ContactIcon />}
            onClick={handleContactAdmin}
            sx={{
              color: '#667eea',
              borderColor: '#667eea',
              textTransform: 'none',
              fontWeight: 'bold',
              paddingX: 3,
              paddingY: 1.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.04)',
                borderColor: '#5a6fd8',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('contact_admin') || 'Contact Admin'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PermissionGuard; 